const DEFAULT_LIBRARIES = ['places'];
let googleMapsPromise = null;

const getConfiguredApiKey = (explicitKey) => {
  if (explicitKey) {
    return explicitKey;
  }

  const envKeys = [
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    process.env.REACT_APP_GOOGLE_API_KEY,
    process.env.REACT_APP_PLACES_API_KEY,
    process.env.REACT_APP_GOOGLE_PLACES_API_KEY,
    process.env.VITE_GOOGLE_API_KEY,
  ];

  return envKeys.find(Boolean) || '';
};

const buildScriptUrl = (apiKey, options = {}) => {
  const params = new URLSearchParams();
  params.set('key', apiKey);

  const libraries = options.libraries && options.libraries.length
    ? Array.from(new Set(options.libraries))
    : DEFAULT_LIBRARIES;

  if (libraries.length) {
    params.set('libraries', libraries.join(','));
  }

  if (options.language) {
    params.set('language', options.language);
  }

  if (options.region) {
    params.set('region', options.region);
  }

  if (options.version) {
    params.set('v', options.version);
  }

  if (options.additionalParams) {
    Object.entries(options.additionalParams).forEach(([key, value]) => {
      if (value != null && value !== '') {
        params.set(key, String(value));
      }
    });
  }

  const baseUrl = 'https://maps.googleapis.com/maps/api/js';
  return `${baseUrl}?${params.toString()}`;
};

const insertGoogleMapsScript = (url) => {
  const existingScript = document.querySelector('script[data-google-maps-loader="true"]');

  if (existingScript) {
    const existingSrc = existingScript.getAttribute('src');
    if (existingSrc === url) {
      return existingScript;
    }

    existingScript.parentNode.removeChild(existingScript);
  }

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.defer = true;
  script.src = url;
  script.setAttribute('data-google-maps-loader', 'true');

  document.head.appendChild(script);
  return script;
};

export const loadGoogleMapsApi = (options = {}) => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps can only be loaded in a browser environment.'));
  }

  if (window.google && window.google.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  const apiKey = getConfiguredApiKey(options.apiKey);

  if (!apiKey) {
    return Promise.reject(
      new Error('Google Maps API key is not configured. Set REACT_APP_GOOGLE_MAPS_API_KEY or a related env variable.')
    );
  }

  const scriptUrl = buildScriptUrl(apiKey, options);

  googleMapsPromise = new Promise((resolve, reject) => {
    const script = insertGoogleMapsScript(scriptUrl);

    script.addEventListener('load', () => {
      if (window.google && window.google.maps) {
        resolve(window.google.maps);
      } else {
        reject(new Error('Google Maps script loaded but window.google.maps is unavailable.'));
      }
    });

    script.addEventListener('error', () => {
      googleMapsPromise = null;
      reject(new Error('Failed to load the Google Maps JavaScript API.'));
    });
  });

  return googleMapsPromise;
};
